import { useEffect, useMemo, useState } from 'react';
import '../../styles/Admin.css';
import { useAuth } from '../../context/AuthContext.jsx';
import AdminPhoto from '../../components/admin/AdminPhoto.jsx';
import ImageLightbox from '../../components/admin/ImageLightbox.jsx';
import {
  downloadImageAs,
  downloadRecordPackage,
  buildImageBaseName,
  buildRecordPhotos,
} from '../../utils/downloads.js';

function RegistrosPage() {
  const { getAuthHeader } = useAuth();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('todos');
  const [query, setQuery] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [adminNotes, setAdminNotes] = useState('');
  const [downloadingId, setDownloadingId] = useState(null);
  const [zoomed, setZoomed] = useState(null);

  const fetchRecords = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/registrations', {
        headers: getAuthHeader(),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Error al cargar registros');
      setRecords(Array.isArray(data.records) ? data.records : []);
    } catch (e) {
      setError(e.message || 'Error de red');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setAdminNotes(selectedProfile?.adminNotes || '');
  }, [selectedProfile]);

  const getStatusClass = (status) => {
    if (status === 'Pendiente') return 'status-badge-pending';
    if (status === 'Aprobado') return 'status-badge-approved';
    return 'status-badge-rejected';
  };

  const filteredRecords = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byTab = activeTab === 'nuevos'
      ? records.filter((r) => r.status === 'Pendiente')
      : records;
    if (!q) return byTab;
    return byTab.filter((r) => {
      const name = String(r.name || '').toLowerCase();
      const city = String(r.city || '').toLowerCase();
      const ig = String(r.instagram || '').toLowerCase();
      return name.includes(q) || city.includes(q) || ig.includes(q);
    });
  }, [activeTab, query, records]);

  const pendingCount = records.filter((r) => r.status === 'Pendiente').length;

  const handleDownloadAll = async (record) => {
    if (!record || downloadingId) return;
    setError('');
    setDownloadingId(record.id);
    try {
      await downloadRecordPackage(record, 'registro');
    } catch (e) {
      setError(e.message || 'No fue posible generar el paquete de descarga');
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadSingle = async (url, label) => {
    if (!url) return;
    try {
      await downloadImageAs(url, buildImageBaseName(selectedProfile, label));
    } catch (e) {
      setError(e.message || 'No fue posible descargar la imagen');
    }
  };

  const recordHasPhotos = (record) =>
    buildRecordPhotos(record, 'registro').length > 0;

  const updateStatus = async (status) => {
    if (!selectedProfile) return;
    setSavingStatus(true);
    setError('');
    try {
      const res = await fetch('/api/registrations', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader(),
        },
        body: JSON.stringify({
          id: selectedProfile.id,
          status,
          adminNotes,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'No fue posible actualizar el estado');

      const updated = data.record;
      setRecords((prev) => prev.map((r) => (r.id === selectedProfile.id ? (updated || { ...r, status, adminNotes }) : r)));
      setSelectedProfile((prev) => (prev ? { ...prev, ...(updated || {}), status, adminNotes } : prev));
    } catch (e) {
      setError(e.message || 'Error de red');
    } finally {
      setSavingStatus(false);
    }
  };

  return (
    <div className="admin-main" style={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
      <header className="admin-registros-header">
        <div>
          <h2 className="admin-registros-title">Gestión de Registros</h2>
          <p className="admin-registros-subtitle">Nuevas aplicaciones pendientes de revisión</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <div style={{ position: 'relative' }}>
            <span className="material-symbols-outlined" style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--admin-primary)', fontSize: '1.25rem' }}>search</span>
            <input type="text" className="admin-input-search" placeholder="Buscar modelo o ciudad..." value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <button type="button" className="admin-btn-primary" onClick={fetchRecords} disabled={loading}>
            <span className="material-symbols-outlined" style={{ fontSize: '0.875rem' }}>download</span>
            {loading ? 'Cargando...' : 'Actualizar'}
          </button>
        </div>
      </header>

      <div className="admin-registros-tabs">
        <button type="button" className={`admin-registros-tab ${activeTab === 'todos' ? 'active' : ''}`} onClick={() => setActiveTab('todos')}>
          Todos <span style={{ marginLeft: '0.5rem', color: 'rgba(212,175,53,0.6)', fontWeight: 500 }}>{records.length}</span>
        </button>
        <button type="button" className={`admin-registros-tab ${activeTab === 'nuevos' ? 'active' : ''}`} onClick={() => setActiveTab('nuevos')}>
          Nuevos <span style={{ marginLeft: '0.5rem', color: 'rgba(255,255,255,0.2)', fontWeight: 500 }}>{pendingCount}</span>
        </button>
      </div>

      <div className="admin-registros-table-wrap admin-scroll no-scrollbar">
        {error && (
          <div className="admin-login-error" style={{ marginBottom: '1rem' }} role="alert">
            {error}
          </div>
        )}
        <table className="admin-registros-table">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Nombre Completo</th>
              <th>Ciudad</th>
              <th>Instagram</th>
              <th>Estado</th>
              <th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((model) => (
              <tr key={model.id}>
                <td style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{model.date}</td>
                <td style={{ fontWeight: 700 }}>{model.name}</td>
                <td style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 500 }}>{model.city}</td>
                <td>
                  <a href="#instagram" className="admin-link-primary">{model.instagram}</a>
                </td>
                <td>
                  <span className={getStatusClass(model.status)}>{model.status}</span>
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div className="admin-row-actions">
                    <button
                      type="button"
                      className="admin-btn-ver admin-btn-download"
                      onClick={() => handleDownloadAll(model)}
                      disabled={!recordHasPhotos(model) || downloadingId === model.id}
                      title={recordHasPhotos(model) ? 'Descargar fotos (ZIP)' : 'Sin fotos disponibles'}
                      aria-label="Descargar todas las fotos"
                    >
                      <span className="ver-text">
                        {downloadingId === model.id ? 'Generando…' : 'Descargar'}
                      </span>
                      <span className="material-symbols-outlined" style={{ fontWeight: 300 }}>
                        {downloadingId === model.id ? 'hourglass' : 'download'}
                      </span>
                    </button>
                    <button type="button" className="admin-btn-ver" onClick={() => setSelectedProfile(model)}>
                      <span className="ver-text">Ver Perfil</span>
                      <span className="material-symbols-outlined" style={{ fontWeight: 300 }}>visibility</span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {!loading && filteredRecords.length === 0 && (
              <tr>
                <td colSpan={6} style={{ color: 'rgba(255,255,255,0.6)', textAlign: 'center' }}>
                  No hay registros para mostrar.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedProfile && (
        <div className="admin-slide-overlay" onClick={() => setSelectedProfile(null)} role="presentation">
          <div className="admin-slide-panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Detalle del registro">
            <header className="admin-slide-header">
              <div>
                <span style={{ fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.3em', color: 'var(--admin-primary)', textTransform: 'uppercase' }}>Talent Registration Detail</span>
                <h2 style={{ fontSize: '1.875rem', fontWeight: 900, lineHeight: 1.2, letterSpacing: '-0.02em', marginTop: '0.25rem' }}>{selectedProfile.name}</h2>
              </div>
              <button type="button" className="admin-slide-close" onClick={() => setSelectedProfile(null)} aria-label="Cerrar">
                <span className="material-symbols-outlined">close</span>
              </button>
            </header>
            <div className="admin-slide-body">
              <section className="admin-slide-section">
                <h3 className="admin-slide-section-title">Profile Information</h3>
                <div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Email</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.email || '-'}</p>
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Teléfono</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.phone || '-'}</p>
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Instagram</p>
                    {selectedProfile.instagram ? (
                      <a href={`https://instagram.com/${selectedProfile.instagram.replace(/^@/, '')}`} target="_blank" rel="noreferrer" className="admin-link-primary" style={{ gridColumn: 'span 2' }}>
                        {selectedProfile.instagram}
                      </a>
                    ) : (
                      <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>-</p>
                    )}
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Ciudad</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.city || '-'}</p>
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Fecha Nacimiento</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.birthDate || '-'}</p>
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Experiencia</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.experience === 'si' ? 'Sí' : selectedProfile.experience === 'no' ? 'No' : (selectedProfile.experience || '-')}</p>
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Cómo llegó</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.source || '-'}</p>
                  </div>
                  <div className="admin-info-row" style={{ display: 'grid', gridTemplateColumns: '1fr 2fr' }}>
                    <p className="admin-info-label">Mensaje</p>
                    <p className="admin-info-value" style={{ gridColumn: 'span 2' }}>{selectedProfile.message || '-'}</p>
                  </div>
                </div>
              </section>
              <section className="admin-slide-section">
                <div className="admin-slide-section-row">
                  <h3 className="admin-slide-section-title">Visual Portfolio Review</h3>
                  <button
                    type="button"
                    className="admin-slide-section-action"
                    onClick={() => handleDownloadAll(selectedProfile)}
                    disabled={!recordHasPhotos(selectedProfile) || downloadingId === selectedProfile.id}
                  >
                    <span className="material-symbols-outlined">
                      {downloadingId === selectedProfile.id ? 'hourglass' : 'download'}
                    </span>
                    {downloadingId === selectedProfile.id ? 'Generando…' : 'Descargar todo (ZIP)'}
                  </button>
                </div>
                <div className="admin-portfolio-grid">
                  <div className="admin-portfolio-item">
                    <label>Rostro</label>
                    <div className="admin-portfolio-box">
                      <AdminPhoto
                        src={selectedProfile.photoFace}
                        alt="Rostro"
                        onZoom={() => setZoomed({ title: 'Rostro', url: selectedProfile.photoFace, label: 'rostro' })}
                        onDownload={() => handleDownloadSingle(selectedProfile.photoFace, 'rostro')}
                      />
                    </div>
                  </div>
                  <div className="admin-portfolio-item">
                    <label>Cuerpo Entero</label>
                    <div className="admin-portfolio-box">
                      <AdminPhoto
                        src={selectedProfile.photoBody}
                        alt="Cuerpo"
                        onZoom={() => setZoomed({ title: 'Cuerpo entero', url: selectedProfile.photoBody, label: 'cuerpo' })}
                        onDownload={() => handleDownloadSingle(selectedProfile.photoBody, 'cuerpo')}
                      />
                    </div>
                  </div>
                </div>
              </section>
              <section className="admin-slide-section">
                <h3 className="admin-slide-section-title" style={{ marginBottom: '1rem' }}>Internal Admin Notes</h3>
                <textarea className="admin-notes-textarea" placeholder="Add internal comments..." rows={5} value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)} />
              </section>
            </div>
            <footer className="admin-slide-footer">
              <button type="button" className="admin-slide-btn outline" onClick={() => updateStatus('Rechazado')} disabled={savingStatus}>Rechazar</button>
              <button type="button" className="admin-slide-btn primary" onClick={() => updateStatus('Pendiente')} disabled={savingStatus}>Pendiente</button>
              <button type="button" className="admin-slide-btn gold" onClick={() => updateStatus('Aprobado')} disabled={savingStatus}>{savingStatus ? 'Guardando...' : 'Aprobar Perfil'}</button>
            </footer>
          </div>
        </div>
      )}

      <ImageLightbox
        open={Boolean(zoomed)}
        title={zoomed?.title}
        url={zoomed?.url}
        onClose={() => setZoomed(null)}
        onDownload={() => handleDownloadSingle(zoomed?.url, zoomed?.label || 'imagen')}
      />
    </div>
  );
}

export default RegistrosPage;
